/**
 * =============================================================================
 *  HINH VE: MOT PHEP TINH HINH HOC DUY NHAT CHO CA MAN HINH LAN TEP
 * =============================================================================
 *
 *  Moi ham o day la ham THUAN TUY tren toa do PDF. Lop hien thi goi chung de ve
 *  ban xem truoc va de bat chuot; duong ghi goi chung de dung /Rect va dong hinh
 *  dang. Do la dieu kien de ban xem truoc khong noi doi.
 *
 *  Bai hoc da tra gia mot lan o duong sua chu: khi ban xem truoc va duong ghi
 *  tinh bang hai doan ma khac nhau, chung troi khoi nhau, va nguoi dung tin vao
 *  cai sai trong hai vong lam viec.
 *
 * =============================================================================
 *  VI SAO DUONG THANG DUOC PHEP CO BE RONG AM
 * =============================================================================
 *  Khung va hinh tron chi can mot hop chu nhat: keo tu goc nao cung ra cung mot
 *  hinh. Duong thang va mui ten thi KHONG: keo tu trai sang phai va keo tu phai
 *  sang trai la hai duong khac nhau, vi mui ten nam o dau CUOI.
 *
 *  Nen voi 'line' va 'arrow', `rect` mang VECTO chu khong mang hop bao:
 *      diem dau  = (x, y)
 *      diem cuoi = (x + width, y + height)
 *  va width/height DUOC PHEP am. Hop bao that (dung cho /Rect, cho vung bam, cho
 *  o chon) luon lay qua bboxOf().
 *
 *  Cach thay the - luu them hai truong toa do - doi sua hop dong kieu va lam moi
 *  chu thich cu thanh khong doc duoc. Cach nay giu hop dong nguyen ven, va cai
 *  gia phai tra la dung MOT ham bboxOf() o moi cho can hop bao.
 */
import type { Annotation, PdfPoint, PdfRect } from '@pdfeditor/types';
import { layoutTextAnnotation, type FontSource } from '../../ui/textBoxMetrics';

/** Loai hinh ve nguoi dung ve bang mot cu keo chuot. */
export type ShapeKind = 'square' | 'circle' | 'line' | 'arrow';

export const SHAPE_KINDS: readonly ShapeKind[] = ['square', 'circle', 'line', 'arrow'];

export function isShapeKind(kind: Annotation['kind']): kind is ShapeKind {
  return (SHAPE_KINDS as readonly string[]).includes(kind);
}

/** Cac loai dung `rect` lam VECTO chu khong phai hop bao. Xem dau tep. */
export function usesVectorRect(kind: Annotation['kind']): boolean {
  return kind === 'line' || kind === 'arrow';
}

/** Be rong net mac dinh khi chu thich khong mang gia tri rieng. */
export const DEFAULT_STROKE_WIDTH_PT = 1.5;

/** Be rong net nho nhat con nhin thay khi in. Duoi muc nay la mot net "toc". */
export const MIN_STROKE_WIDTH_PT = 0.25;
export const MAX_STROKE_WIDTH_PT = 24;

/**
 * Be rong net THUC SU dung cho mot chu thich.
 *
 * MOT ham, goi tu ca dong hinh dang, khoa /BS, va lop hien thi. Ba cho tu doc
 * `annotation.strokeWidthPt ?? 1.5` la ba cho de troi khoi nhau.
 */
export function strokeWidthOf(annotation: Annotation, fallback = DEFAULT_STROKE_WIDTH_PT): number {
  const w = annotation.strokeWidthPt;
  if (typeof w !== 'number' || !Number.isFinite(w)) return fallback;
  return Math.min(MAX_STROKE_WIDTH_PT, Math.max(MIN_STROKE_WIDTH_PT, w));
}

/** Hinh co to ben trong khong. `null`/vang mat = KHONG to - xem hop dong kieu. */
export function fillOf(annotation: Annotation): readonly [number, number, number] | null {
  const fill = annotation.fillRgb;
  if (!fill) return null;
  if (fill.length !== 3 || !fill.every((c) => Number.isFinite(c) && c >= 0 && c <= 1)) return null;
  return fill;
}

/** Hop chu nhat luon co be rong / chieu cao KHONG AM. */
export function normalizeRect(rect: PdfRect): PdfRect {
  return {
    x: rect.width < 0 ? rect.x + rect.width : rect.x,
    y: rect.height < 0 ? rect.y + rect.height : rect.y,
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  };
}

/**
 * HOP BAO cua mot chu thich - o chon, vung bam, va /Rect deu lay tu day.
 *
 * Voi duong thang / mui ten, hop bao duoc NOI RONG them `pad`: dau mui ten tran
 * ra ngoai hai dau mut, va mot hop bao om sat vecto se cat cut chinh dau mui ten
 * do trong /BBox cua dong hinh dang.
 */
export function bboxOf(annotation: Annotation, pad = 0): PdfRect {
  const base = normalizeRect(annotation.rect);
  if (pad <= 0) return base;
  return { x: base.x - pad, y: base.y - pad, width: base.width + pad * 2, height: base.height + pad * 2 };
}

/** Hai dau mut that cua mot duong thang / mui ten. */
export function endpointsOf(rect: PdfRect): { from: PdfPoint; to: PdfPoint } {
  return {
    from: { x: rect.x, y: rect.y },
    to: { x: rect.x + rect.width, y: rect.y + rect.height },
  };
}

/**
 * MOT CU KEO CHUOT -> HINH CHU NHAT CUA CHU THICH.
 *
 * `constrain` la phim Shift dang giu:
 *    - khung / hinh tron / anh: ep thanh hinh vuong (canh = canh dai hon)
 *    - duong thang / mui ten:   ep ve boi so 45 do
 *
 * Ham nay la NGUON DUY NHAT sinh ra hinh chu nhat tu mot cu keo. Ban xem truoc
 * luc dang keo goi chinh no, nen thu nguoi dung thay trong luc keo la dung thu
 * duoc ghi ra khi nha chuot.
 */
export function dragToRect(
  start: PdfPoint,
  end: PdfPoint,
  kind: Annotation['kind'],
  constrain = false,
): PdfRect {
  if (usesVectorRect(kind)) {
    let dx = end.x - start.x;
    let dy = end.y - start.y;
    if (constrain) {
      // Chieu vecto len boi so 45 do gan nhat, GIU NGUYEN do dai. Lam tron goc
      // roi dung lai do dai cu la cach duy nhat khong lam duong ngan/dai di khi
      // nguoi dung bam Shift giua chung.
      const length = Math.hypot(dx, dy);
      const snapped = (Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * Math.PI) / 4;
      dx = Math.cos(snapped) * length;
      dy = Math.sin(snapped) * length;
    }
    return { x: start.x, y: start.y, width: dx, height: dy };
  }

  let width = end.x - start.x;
  let height = end.y - start.y;
  if (constrain) {
    // Canh dai hon quyet dinh, de hinh vuong luon phu duoc vung nguoi dung keo.
    const side = Math.max(Math.abs(width), Math.abs(height));
    width = Math.sign(width || 1) * side;
    height = Math.sign(height || 1) * side;
  }
  return normalizeRect({ x: start.x, y: start.y, width, height });
}

// ---------------------------------------------------------------------------
//  Bam trung
// ---------------------------------------------------------------------------

/**
 * VUNG BAM RONG HON NET VE - va day khong phai su cau tha.
 *
 * Mot duong thang day 1 diem la mot muc tieu rong 1/72 inch. Voi chuot thi kho,
 * voi ngon tay tren man hinh cam ung thi gan nhu khong the. Moi bo cong cu ve
 * nghiem tuc deu noi rong vung bam quanh net mong, va nguoi dung khong bao gio
 * biet dieu do dang xay ra - ho chi thay "bam la trung".
 *
 * Con so tinh bang DIEM cua trang, khong phai diem anh man hinh: o muc thu nho
 * 50% ban se can vung bam rong gap doi tren man hinh, va di theo don vi trang
 * la tu dong dung duoc dieu do.
 */
export const MIN_HIT_TOLERANCE_PT = 4;

/**
 * NOI RONG THEM cho net ve tay, ngoai dung sai chung.
 *
 * Mot net ve tay khong phai mot hinh: no la mot duong gap khuc mong, thuong
 * cong, va nguoi dung nho lai "cho toi da ve" chu khong nho tung diem. Dung sai
 * cua khung va hinh tron do duoc vi chung co canh thang de nham; mot net nguech
 * ngoac thi khong. Con so nay la khoan bu cho dieu do, va no la ly do mot cu bam
 * "gan dung" van chon duoc net vua ve.
 */
export const INK_HIT_EXTRA_PT = 3;

/** Khoang cach tu mot diem den mot doan thang. */
export function distanceToSegment(p: PdfPoint, a: PdfPoint, b: PdfPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Diem co bam trung chu thich nay khong.
 *
 * Khung va hinh tron KHONG to thi chi bam trung o VIEN - bam vao giua mot khung
 * rong phai roi xuong thu nam duoi no, dung nhu moi trinh ve khac. Co to ben
 * trong thi ca vung trong deu bam duoc, vi luc do hinh that su che cho do.
 */
export function hitTest(annotation: Annotation, point: PdfPoint, tolerancePt = MIN_HIT_TOLERANCE_PT): boolean {
  const tolerance = Math.max(tolerancePt, strokeWidthOf(annotation) / 2 + 1);

  if (usesVectorRect(annotation.kind)) {
    const { from, to } = endpointsOf(annotation.rect);
    return distanceToSegment(point, from, to) <= tolerance;
  }

  // VE TAY: bam theo CHINH NET, khong theo hop bao.
  //
  // Duong cu di qua nhanh hop chu nhat ben duoi, va vi mot net ve tay khong co
  // mau to nen no roi vao nhanh "rong ruot": chi trung khi bam gan VIEN CUA HOP
  // BAO. Voi mot net cong, vien hop bao la mot cho khong he co muc - nguoi dung
  // bam dung vao net minh vua ve va khong chon duoc gi. Do la ly do net ve tay
  // la loai duy nhat khong bao gio chon duoc sau khi ve.
  if (annotation.kind === 'ink') {
    const paths = (annotation.inkPaths ?? []).filter((p) => p.length > 0);
    if (paths.length > 0) {
      const inkTolerance = tolerance + INK_HIT_EXTRA_PT;
      for (const path of paths) {
        const head = path[0] as PdfPoint;
        if (path.length === 1) {
          if (Math.hypot(point.x - head.x, point.y - head.y) <= inkTolerance) return true;
          continue;
        }
        for (let i = 1; i < path.length; i++) {
          const a = path[i - 1] as PdfPoint;
          const b = path[i] as PdfPoint;
          if (distanceToSegment(point, a, b) <= inkTolerance) return true;
        }
      }
      return false;
    }
    // Khong con diem nao (du lieu hong): lui ve hop bao chu khong tra ve "khong
    // trung" - mot doi tuong khong bam duoc la mot doi tuong khong xoa duoc.
  }

  const r = normalizeRect(annotation.rect);
  const outside =
    point.x < r.x - tolerance ||
    point.x > r.x + r.width + tolerance ||
    point.y < r.y - tolerance ||
    point.y > r.y + r.height + tolerance;
  if (outside) return false;

  // Doi tuong CHIEM CA VUNG cua no, khong chi cai vien.
  //
  // Hop chu tu do nam trong danh sach nay du no khong to nen va (tu khi bo vien)
  // khong ve gi ngoai chu: neu no chi bam trung o vien thi mot hop chu KHONG CO
  // VIEN se thanh mot doi tuong khong con canh nao de bam - khong chon duoc,
  // khong di chuyen duoc, khong xoa duoc. Ca hop la vung bam.
  const filled =
    fillOf(annotation) !== null ||
    annotation.kind === 'stamp' ||
    isTextBoxKind(annotation.kind);
  if (filled) return true;

  // Rong ruot: chi trung neu diem nam gan VIEN.
  const insideCore =
    point.x > r.x + tolerance &&
    point.x < r.x + r.width - tolerance &&
    point.y > r.y + tolerance &&
    point.y < r.y + r.height - tolerance;
  if (annotation.kind === 'circle') {
    // Voi hinh tron, "gan vien" do bang phuong trinh ellipse chuan hoa, khong
    // bang hop chu nhat: bam vao mot goc cua hop bao KHONG duoc coi la trung
    // hinh tron - o do khong co gi ca.
    const rx = r.width / 2;
    const ry = r.height / 2;
    if (rx <= 0 || ry <= 0) return !insideCore;
    const nx = (point.x - (r.x + rx)) / rx;
    const ny = (point.y - (r.y + ry)) / ry;
    const d = Math.hypot(nx, ny);
    const band = tolerance / Math.min(rx, ry);
    return Math.abs(d - 1) <= band;
  }
  return !insideCore;
}

/** Chu thich TREN CUNG tai mot diem. Danh sach ve theo thu tu, cai sau nam tren. */
export function pickAt(
  annotations: readonly Annotation[],
  point: PdfPoint,
  tolerancePt = MIN_HIT_TOLERANCE_PT,
): Annotation | null {
  for (let i = annotations.length - 1; i >= 0; i--) {
    const a = annotations[i] as Annotation;
    if (hitTest(a, point, tolerancePt)) return a;
  }
  return null;
}

// ---------------------------------------------------------------------------
//  Tay nam de doi kich thuoc
// ---------------------------------------------------------------------------

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const HANDLE_IDS: readonly HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/**
 * Tay nam duoc phep hien cho MOT loai doi tuong.
 *
 * Doi tuong mang chu (ghi chu, hop chu) co chieu cao SUY RA TU NOI DUNG: bop
 * hep lai thi chu xuong dong va hop CAO LEN. Keo canh tren hay canh duoi cua
 * chung khong co nghia gi, va duong ghi tu choi thay doi do.
 *
 * Truoc day ta van ve du tam tay nam roi tu choi trong im lang: con tro doi
 * thanh mui ten hai chieu, nguoi dung keo, khong co gi xay ra, va khong co loi
 * giai thich nao. Duoi mat ho do la mot tay nam HONG. Thu gi tu choi hanh dong
 * thi khong duoc moi goi hanh dong do.
 */
export function handleIdsFor(kind: string): readonly HandleId[] {
  const heightFromContent = kind === 'text-note' || kind === 'free-text';
  if (!heightFromContent) return HANDLE_IDS;
  return HANDLE_IDS.filter((id) => id !== 'n' && id !== 's');
}

/**
 * Tam cua tam tay nam, TOA DO PDF.
 *
 * 'n' la canh TREN trong toa do PDF, tuc la y lon. Lop hien thi doi sang toa do
 * DOM qua pdfRectToCss() nhu moi thu khac - khong co phep tru y nao o day.
 */
export function handlePoints(rect: PdfRect): Record<HandleId, PdfPoint> {
  const r = normalizeRect(rect);
  const midX = r.x + r.width / 2;
  const midY = r.y + r.height / 2;
  const top = r.y + r.height;
  const right = r.x + r.width;
  return {
    nw: { x: r.x, y: top },
    n: { x: midX, y: top },
    ne: { x: right, y: top },
    e: { x: right, y: midY },
    se: { x: right, y: r.y },
    s: { x: midX, y: r.y },
    sw: { x: r.x, y: r.y },
    w: { x: r.x, y: midY },
  };
}

/**
 * Tay nam duoi con tro, hoac null.
 *
 * `kind` de loai bo nhung tay nam khong duoc phep cho loai doi tuong do - xem
 * handleIdsFor(). Bo qua `kind` thi xet ca tam, giu nguyen hanh vi cu.
 */
export function handleAt(
  rect: PdfRect,
  point: PdfPoint,
  tolerancePt: number,
  kind?: string,
): HandleId | null {
  const points = handlePoints(rect);
  let best: HandleId | null = null;
  let bestDistance = tolerancePt;
  for (const id of kind === undefined ? HANDLE_IDS : handleIdsFor(kind)) {
    const p = points[id];
    const d = Math.hypot(point.x - p.x, point.y - p.y);
    if (d <= bestDistance) {
      best = id;
      bestDistance = d;
    }
  }
  return best;
}

/** Con tro CSS cho tung tay nam. */
export const HANDLE_CURSOR: Record<HandleId, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
};

/**
 * KICH THUOC NHO NHAT mot doi tuong duoc phep co sau khi keo tay nam.
 *
 * Khong phai con so lam dep. Mot hop 1x1 diem la mot doi tuong KHONG BAM TRUNG
 * DUOC nua: vung bam cua no nho hon dung sai cua chinh phep bam, nen nguoi dung
 * keo hut tay mot cai la mat luon kha nang chon lai de xoa hay hoan tac bang
 * chuot. 8 diem la vao khoang o tay nam tren man hinh o muc thu phong 100%, tuc
 * la nho nhat ma van con cam duoc.
 */
export const MIN_OBJECT_SIZE_PT = 8;

/**
 * Rong nho nhat cua mot HOP CHU. Rong hon muc chung, va co ly do:
 * hop chu con phai chua duoc le trong hai ben (2 x TEXT_BOX_PADDING_PT) va it
 * nhat mot chu cai. Bop toi 8 diem thi phan dat chu con am, va viec ngat dong
 * se cho ra mot dong cho moi ky tu.
 */
export const MIN_TEXT_BOX_WIDTH_PT = 32;

/** Doi tuong nay co chu ben trong va tu do chieu cao hop khong. */
export function isTextBoxKind(kind: Annotation['kind']): boolean {
  return kind === 'free-text' || kind === 'text-note';
}

/**
 * Keo mot tay nam den `point`.
 *
 * `keepAspect` giu dung ti le cua hinh chu nhat BAN DAU - anh dung no lam mac
 * dinh, con hinh ve thi khong. Chi cac tay nam O GOC giu duoc ti le; keo canh
 * giua ma van ep ti le se lam hinh nhay theo chieu con lai mot cach kho hieu.
 *
 * HINH KHONG BAO GIO LAT NGUOC. Keo tay nam trai vuot qua mep phai cho ra mot
 * hop van duong - normalizeRect() o giua lo cho do. Va khi cham san kich thuoc,
 * MEP DOI DIEN voi tay nam dang keo la mep dung yen: neu chi ep be rong ma de
 * nguyen x, hop se bo ra xa khoi diem neo dung luc nguoi dung dang bop no lai.
 */
export function resizeRect(
  rect: PdfRect,
  handle: HandleId,
  point: PdfPoint,
  options: { keepAspect?: boolean; minSizePt?: number } = {},
): PdfRect {
  const r = normalizeRect(rect);
  const minSize = Math.max(0, options.minSizePt ?? MIN_OBJECT_SIZE_PT);
  let x0 = r.x;
  let y0 = r.y;
  let x1 = r.x + r.width;
  let y1 = r.y + r.height;

  if (handle === 'nw' || handle === 'w' || handle === 'sw') x0 = point.x;
  if (handle === 'ne' || handle === 'e' || handle === 'se') x1 = point.x;
  if (handle === 'nw' || handle === 'n' || handle === 'ne') y1 = point.y;
  if (handle === 'sw' || handle === 's' || handle === 'se') y0 = point.y;

  let out = normalizeRect({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 });

  const corner = handle === 'nw' || handle === 'ne' || handle === 'se' || handle === 'sw';
  if (options.keepAspect && corner && r.width > 0 && r.height > 0) {
    const ratio = r.width / r.height;
    // Chieu NAO NOI RONG HON quyet dinh, de hinh luon phu duoc cu keo.
    const byWidth = out.width / ratio >= out.height;
    const width = byWidth ? out.width : out.height * ratio;
    const height = byWidth ? out.width / ratio : out.height;
    // Goc DOI DIEN voi tay nam dang keo phai dung yen.
    const anchorLeft = handle === 'nw' || handle === 'sw' ? out.x + out.width : out.x;
    const anchorBottom = handle === 'sw' || handle === 'se' ? out.y + out.height : out.y;
    const left = handle === 'nw' || handle === 'sw' ? anchorLeft - width : anchorLeft;
    const bottom = handle === 'sw' || handle === 'se' ? anchorBottom - height : anchorBottom;
    out = { x: left, y: bottom, width, height };
  }

  // SAN KICH THUOC, ap quanh MEP DUNG YEN chu khong quanh goc duoi-trai.
  //
  // Tay nam trai ('nw','w','sw') keo ve phia mep phai: mep phai la mep dung yen,
  // nen khi cham san ta giu no va day x sang trai. Lam nguoc lai - giu x va noi
  // be rong - se khien hop TRUOT SANG PHAI dung luc nguoi dung dang bop no lai,
  // va tren man hinh no trong nhu hop dang bo chay khoi con tro.
  const width = Math.max(minSize, out.width);
  const height = Math.max(minSize, out.height);
  const holdsRight = handle === 'nw' || handle === 'w' || handle === 'sw';
  const holdsTop = handle === 'sw' || handle === 's' || handle === 'se';
  return {
    x: holdsRight ? out.x + out.width - width : out.x,
    y: holdsTop ? out.y + out.height - height : out.y,
    width,
    height,
  };
}

/**
 * Ket qua doi kich thuoc, ap NGUOC lai vao `annotation.rect`.
 *
 * Voi duong thang / mui ten, hop bao khong du de dung lai vecto: mot hop bao
 * ung voi bon huong. Nen ta giu HUONG cua vecto cu va chi co gian do dai - keo
 * mot mui ten to ra khong duoc lam no quay dau lai.
 */
export function applyResize(
  annotation: Annotation,
  handle: HandleId,
  point: PdfPoint,
  options: { keepAspect?: boolean; minSizePt?: number } = {},
): PdfRect {
  const resized = resizeRect(bboxOf(annotation), handle, point, options);
  if (!usesVectorRect(annotation.kind)) return resized;

  const signX = Math.sign(annotation.rect.width) || 1;
  const signY = Math.sign(annotation.rect.height) || 1;
  return {
    x: signX > 0 ? resized.x : resized.x + resized.width,
    y: signY > 0 ? resized.y : resized.y + resized.height,
    width: resized.width * signX,
    height: resized.height * signY,
  };
}

/**
 * =============================================================================
 *  KET QUA CUOI CUNG CUA MOT LAN KEO TAY NAM - MOT HAM, MOI BEN GOI
 * =============================================================================
 *
 *  Ban xem truoc trong luc keo, gia tri ghi vao lenh khi nha tay, va o chon ve
 *  quanh hinh: CA BA goi ham nay. Do khong phai su sach se ve kien truc, do la
 *  dieu kien de o chon khong noi doi - xem ghi chu dau tep.
 *
 * =============================================================================
 *  VI SAO HOP CHU KHONG KEO CAO THAP DUOC
 * =============================================================================
 *  Voi 'free-text' va ghi chu dang hop, chieu cao KHONG phai thu nguoi dung
 *  chon. No duoc DO ra tu noi dung qua layoutTextAnnotation() - chinh ham ma
 *  duong ghi dung de dung /AP. Keo tay nam chi doi BE RONG; chu ngat lai trong
 *  be rong moi va chieu cao tu tra loi.
 *
 *  Cho phep keo chieu cao xuong thap hon so do se cho ra dung mot trong hai ket
 *  qua, va ca hai deu la loi:
 *    - man hinh cat chu con tep xuat ra thi khong (hoac nguoc lai), tuc la ban
 *      xem truoc noi doi mot lan nua;
 *    - hoac ca hai cung cat, va thu bi cat truoc tien la dau thanh cua tieng
 *      Viet - "Ề" thanh "E" - mot kieu mat du lieu am tham nhin qua khong thay.
 *  Nen o day no TU CHOI thu hep: mep TREN cua hop dung yen, chieu cao do lai.
 *
 *  `font` vang mat (font chua nap xong) thi hop chu duoc doi xu nhu moi hinh
 *  khac. Tha keo duoc bang hinh hoc tho con hon dong bang tay nam lai.
 */
export function resolveResize(
  annotation: Annotation,
  handle: HandleId,
  point: PdfPoint,
  options: { keepAspect?: boolean; font?: FontSource | null } = {},
): PdfRect {
  const textBox = isTextBoxKind(annotation.kind);
  const resized = applyResize(annotation, handle, point, {
    ...(options.keepAspect !== undefined ? { keepAspect: options.keepAspect } : {}),
    minSizePt: textBox ? MIN_TEXT_BOX_WIDTH_PT : MIN_OBJECT_SIZE_PT,
  });
  const font = options.font;
  if (!font || !textBox) return resized;

  // Mep TREN cua hop cu, giu nguyen. layoutTextAnnotation() neo o goc tren-trai
  // va moc chu XUONG tu do, nen dua vao mot hop cao 0 tai dung mep do la cach
  // noi "giu dinh o day, chieu cao thi anh do lay".
  const top = annotation.rect.y + annotation.rect.height;
  return layoutTextAnnotation(font, {
    ...annotation,
    rect: { x: resized.x, y: top, width: resized.width, height: 0 },
  }).rect;
}

/** Doi cho mot chu thich. Vecto duoc giu nguyen; chi diem goc di chuyen. */
export function moveRect(rect: PdfRect, dx: number, dy: number): PdfRect {
  return { x: rect.x + dx, y: rect.y + dy, width: rect.width, height: rect.height };
}

/**
 * =============================================================================
 *  NET VE TAY: `rect` KHONG PHAI HINH DANG, `inkPaths` MOI LA
 * =============================================================================
 *
 *  Voi moi loai khac, `rect` LA hinh dang: doi `rect` la doi cai duoc ve. Voi
 *  'ink' thi khong - hinh dang nam trong `inkPaths`, con `rect` chi la hop bao
 *  di theo. Ca lop hien thi lan duong ghi (/InkList, va /Rect ma write.ts tinh
 *  LAI tu chinh cac net) deu doc `inkPaths`.
 *
 *  Nen mot lenh chi doi `rect` cua net ve tay la mot lenh KHONG LAM GI CA: o
 *  chon nhay sang cho moi, net van nam nguyen cho cu, va tep xuat ra giu vi tri
 *  cu. Ham nay la cho duy nhat dich `rect` moi thanh mot phep bien doi that su
 *  tren cac diem.
 */
export function transformInkPaths(annotation: Annotation, nextRect: PdfRect): PdfPoint[][] | null {
  if (annotation.kind !== 'ink') return null;
  const paths = annotation.inkPaths;
  if (!paths || paths.length === 0) return null;

  const from = normalizeRect(annotation.rect);
  const to = normalizeRect(nextRect);
  // Hop bao bet (net thang dung hoac thang ngang) khong co ti le de co gian
  // theo chieu do - giu he so 1 thay vi chia cho 0 va sinh ra NaN.
  const sx = from.width > 1e-6 ? to.width / from.width : 1;
  const sy = from.height > 1e-6 ? to.height / from.height : 1;
  if (sx === 1 && sy === 1 && to.x === from.x && to.y === from.y) return null;

  return paths.map((path) =>
    path.map((p) => ({
      x: to.x + (p.x - from.x) * sx,
      y: to.y + (p.y - from.y) * sy,
    })),
  );
}

/**
 * MOT LAN KEO DI CHO / DOI KICH THUOC -> BAN VA CUA MOT lenh annotation.update.
 *
 * Ton tai de ben goi khong phai nho rang net ve tay can mot truong thu hai. Moi
 * loai khac tra ve dung `{ rect }`.
 */
export function rectChangePatch(annotation: Annotation, rect: PdfRect): Partial<Annotation> {
  const inkPaths = transformInkPaths(annotation, rect);
  return inkPaths ? { rect, inkPaths } : { rect };
}

/** Giu chu thich khong bi keo ra khoi hop giay. Chua toi thieu mot goc con thay duoc. */
export function clampToPage(rect: PdfRect, pageWidthPt: number, pageHeightPt: number): PdfRect {
  const box = normalizeRect(rect);
  const margin = Math.min(12, box.width, box.height);
  const dx =
    box.x > pageWidthPt - margin
      ? pageWidthPt - margin - box.x
      : box.x + box.width < margin
        ? margin - (box.x + box.width)
        : 0;
  const dy =
    box.y > pageHeightPt - margin
      ? pageHeightPt - margin - box.y
      : box.y + box.height < margin
        ? margin - (box.y + box.height)
        : 0;
  return moveRect(rect, dx, dy);
}
